import { useState } from 'react';
// import { useEffect, useRef } from 'react';
// import Player from '@vimeo/player';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useRecordAcknowledgement } from '@/hooks/useUnclaimed';
import type { AcknowledgementStatus } from '@/hooks/useUnclaimed';

/** Emphasis on rule consequences — brand accent (light) / brand orange (dark). */
const RULE_EMPHASIS = 'text-brand-orange-accent dark:text-brand-orange-400';

interface UnclaimedAcknowledgementGateProps {
  status: AcknowledgementStatus;
  onAcknowledged: () => void;
}

/**
 * Spec — Unclaimed Commission.docx (Access Restriction):
 *   "User must accept the rules before the Unclaimed Commission report
 *    is unlocked."
 *
 * NOTE: Video gate is commented out for now. When the training video is
 * ready, uncomment the Vimeo player block and re-enable the video-
 * completion prerequisite on the checkbox / accept button.
 */
export function UnclaimedAcknowledgementGate({
  status: _status,
  onAcknowledged,
}: UnclaimedAcknowledgementGateProps) {
  // -----------------------------------------------------------------------
  // VIDEO GATE — commented out for future use
  // -----------------------------------------------------------------------
  // const playerContainerRef = useRef<HTMLDivElement>(null);
  // const playerRef = useRef<Player | null>(null);
  // const watchedAtRef = useRef<Date | null>(null);
  //
  // const isPlaceholderVideo =
  //   !status.videoId || status.videoId === 'placeholder-video-id';
  //
  // const videoUrl: `https://vimeo.com/${string}` | null = isPlaceholderVideo
  //   ? null
  //   : `https://vimeo.com/${status.videoId.trim()}`;
  //
  // const [videoCompleted, setVideoCompleted] = useState(isPlaceholderVideo);
  //
  // useEffect(() => {
  //   if (isPlaceholderVideo) return;
  //   if (!playerContainerRef.current) return;
  //   if (!videoUrl) return;
  //
  //   const player = new Player(playerContainerRef.current, {
  //     url: videoUrl,
  //     responsive: true,
  //     title: false,
  //     byline: false,
  //     portrait: false,
  //   });
  //
  //   const markComplete = () => {
  //     if (!watchedAtRef.current) {
  //       watchedAtRef.current = new Date();
  //     }
  //     setVideoCompleted(true);
  //   };
  //
  //   const onTimeUpdate = (data: { seconds: number; duration: number }) => {
  //     if (data.duration > 0 && data.duration - data.seconds <= 10) {
  //       markComplete();
  //     }
  //   };
  //
  //   player.on('ended', markComplete);
  //   player.on('timeupdate', onTimeUpdate);
  //   playerRef.current = player;
  //
  //   return () => {
  //     try {
  //       player.off('ended', markComplete);
  //       player.off('timeupdate', onTimeUpdate);
  //       player.destroy();
  //     } catch {
  //       /* unmount race — safe to ignore */
  //     }
  //     playerRef.current = null;
  //   };
  // }, [videoUrl, isPlaceholderVideo]);
  // -----------------------------------------------------------------------

  const [accepted, setAccepted] = useState(false);
  const { toast } = useToast();
  const recordAck = useRecordAcknowledgement();

  const acceptDisabled = !accepted || recordAck.isPending;

  const handleAccept = async () => {
    if (!accepted || recordAck.isPending) return;
    try {
      await recordAck.mutateAsync({
        videoWatchedAt: new Date(),
        acceptedTerms: accepted,
      });
      toast({
        title: 'Access granted',
        description: 'Welcome to the Unclaimed Commissions report.',
      });
      onAcknowledged();
    } catch (err) {
      toast({
        title: 'Could not record acknowledgement',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-100">
          Access the Unclaimed Commission Report
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Before viewing the unclaimed commission report, read and accept the
          rules below.
        </p>
      </div>

      {/* -------------------------------------------------------------------
       * VIDEO PLAYER — commented out for future use
       * Uncomment when the training video is ready.
       * -------------------------------------------------------------------
       * <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-black dark:border-gray-700">
       *   {isPlaceholderVideo ? (
       *     <div className="flex h-64 items-center justify-center text-sm text-gray-400">
       *       Training video not configured (UNCLAIMED_VIDEO_ID env var unset).
       *       <br />
       *       Watch-completion gate bypassed for local QA.
       *     </div>
       *   ) : (
       *     <div ref={playerContainerRef} className="aspect-video w-full" />
       *   )}
       * </div>
       * ----------------------------------------------------------------- */}

      <div className="mt-6 space-y-5 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Rules
        </h2>

        <section className="space-y-2">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            Never Claim A Commission That Does Not Belong To You
          </h3>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              If you claim someone else&apos;s commission accidentally by
              checking the wrong checkbox, you will receive a warning. After a
              second accidental claim,{' '}
              <span className={RULE_EMPHASIS}>
                you will no longer be able to access the Unclaimed Commission
                Report.
              </span>
            </li>
            <li>
              If you claim someone else&apos;s commission and fraudulently create
              an invoice for someone else&apos;s booking you will be{' '}
              <span className={RULE_EMPHASIS}>
                restricted from the Unclaimed Commission Report
              </span>
              , your membership agreement will be subject to termination, in
              addition to any further actions permitted under the membership
              agreement. Please don&apos;t do it!
            </li>
          </ul>
        </section>

        <section className="space-y-2">
          <h3 className="font-semibold text-gray-900 dark:text-gray-100">
            All Claimed Commission Payments Must Have A Corresponding Invoice
          </h3>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              If you previously created an invoice for this booking, we will{' '}
              <span className={RULE_EMPHASIS}>
                correct any incorrect values on the invoice for you.
              </span>{' '}
              You do <strong>not</strong> need to void the original invoice and
              create a new corrected one.
            </li>
            <li>
              If you did <strong>not</strong> previously submit a sale to create
              an invoice for this booking, you need to create an invoice with all
              required information before claiming the commission.
            </li>
            <li className={RULE_EMPHASIS}>
              Any invoice created after the payment date may be subject to a $25
              late fee, depending on your GEM status.
            </li>
          </ul>
        </section>
      </div>

      <div className="mt-6 flex items-start gap-3">
        <Checkbox
          id="accept-unclaimed-terms"
          checked={accepted}
          onCheckedChange={(checked) => setAccepted(checked === true)}
        />
        <label
          htmlFor="accept-unclaimed-terms"
          className="text-sm leading-tight text-gray-700 dark:text-gray-300"
        >
          I have read and agree to the rules above.
        </label>
      </div>

      <div className="mt-6">
        <Button onClick={handleAccept} disabled={acceptDisabled}>
          {recordAck.isPending ? 'Recording…' : 'Accept and Continue'}
        </Button>
      </div>
    </div>
  );
}
